/**
 * Tracking Service for Meta Pixel and Google Analytics
 * Handles all tracking events for public forms
 */

class TrackingService {
  constructor() {
    this.isMetaPixelInitialized = false;
    this.isGoogleAnalyticsInitialized = false;
    this.debug = process.env.NODE_ENV === 'development';
  }

  /**
   * Initialize Meta Pixel
   * @param {string} pixelId - Facebook/Meta Pixel ID
   */
  initializeMetaPixel(pixelId) {
    if (!pixelId || this.isMetaPixelInitialized) return;

    try {
      // Check if fbq is already loaded
      if (typeof window !== 'undefined' && !window.fbq) {
        // Initialize Facebook Pixel
        (function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)})(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
      }

      window.fbq('init', pixelId);
      this.isMetaPixelInitialized = true;
      
      if (this.debug) {
        console.log('Meta Pixel initialized:', pixelId);
      }
    } catch (error) {
      console.error('Error initializing Meta Pixel:', error);
    }
  }

  /**
   * Initialize Google Analytics 4
   * @param {string} measurementId - Google Analytics Measurement ID
   */
  initializeGoogleAnalytics(measurementId) {
    if (!measurementId || this.isGoogleAnalyticsInitialized) return;

    try {
      // Check if gtag is already loaded
      if (typeof window !== 'undefined' && !window.gtag) {
        // Load Google Analytics
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        window.gtag = function() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
      }

      window.gtag('config', measurementId);
      this.isGoogleAnalyticsInitialized = true;
      
      if (this.debug) {
        console.log('Google Analytics initialized:', measurementId);
      }
    } catch (error) {
      console.error('Error initializing Google Analytics:', error);
    }
  }

  /**
   * Track form view event
   * @param {string} formId - Unique form identifier
   * @param {string} formTitle - Form title
   * @param {object} additionalData - Additional data to track
   */
  trackFormView(formId, formTitle, additionalData = {}) {
    try {
      // Meta Pixel tracking
      if (this.isMetaPixelInitialized && window.fbq) {
        window.fbq('track', 'ViewContent', {
          content_name: formTitle,
          content_ids: [formId],
          content_type: 'form',
          ...additionalData
        });
      }

      // Google Analytics tracking
      if (this.isGoogleAnalyticsInitialized && window.gtag) {
        window.gtag('event', 'view_item', {
          currency: 'USD',
          value: 0,
          items: [{
            item_id: formId,
            item_name: formTitle,
            item_category: 'form',
            ...additionalData
          }]
        });
      }

      if (this.debug) {
        console.log('Tracked form view:', { formId, formTitle, additionalData });
      }
    } catch (error) {
      console.error('Error tracking form view:', error);
    }
  }

  /**
   * Track form submission start event
   * @param {string} formId - Unique form identifier
   * @param {string} formTitle - Form title
   * @param {object} additionalData - Additional data to track
   */
  trackFormStart(formId, formTitle, additionalData = {}) {
    try {
      // Meta Pixel tracking
      if (this.isMetaPixelInitialized && window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          content_name: formTitle,
          content_ids: [formId],
          content_type: 'form',
          ...additionalData
        });
      }

      // Google Analytics tracking
      if (this.isGoogleAnalyticsInitialized && window.gtag) {
        window.gtag('event', 'begin_checkout', {
          currency: 'USD',
          value: 0,
          items: [{
            item_id: formId,
            item_name: formTitle,
            item_category: 'form',
            ...additionalData
          }]
        });
      }

      if (this.debug) {
        console.log('Tracked form start:', { formId, formTitle, additionalData });
      }
    } catch (error) {
      console.error('Error tracking form start:', error);
    }
  }

  /**
   * Track form submission event
   * @param {string} formId - Unique form identifier
   * @param {string} formTitle - Form title
   * @param {string} submissionId - Submission ID
   * @param {object} formData - Form data (optional)
   * @param {object} additionalData - Additional data to track
   */
  trackFormSubmission(formId, formTitle, submissionId, formData = {}, additionalData = {}) {
    try {
      // Meta Pixel tracking
      if (this.isMetaPixelInitialized && window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: formTitle,
          content_ids: [formId],
          content_type: 'form',
          submission_id: submissionId,
          ...additionalData
        });

        // Also track as custom event for more detailed analytics
        window.fbq('trackCustom', 'FormSubmission', {
          form_id: formId,
          form_title: formTitle,
          submission_id: submissionId,
          ...additionalData
        });
      }

      // Google Analytics tracking
      if (this.isGoogleAnalyticsInitialized && window.gtag) {
        window.gtag('event', 'generate_lead', {
          currency: 'USD',
          value: 0,
          form_id: formId,
          form_title: formTitle,
          submission_id: submissionId,
          ...additionalData
        });

        // Also track as custom event
        window.gtag('event', 'form_submission', {
          form_id: formId,
          form_title: formTitle,
          submission_id: submissionId,
          event_category: 'Form',
          event_label: formTitle,
          ...additionalData
        });
      }

      if (this.debug) {
        console.log('Tracked form submission:', { 
          formId, 
          formTitle, 
          submissionId, 
          additionalData 
        });
      }
    } catch (error) {
      console.error('Error tracking form submission:', error);
    }
  }

  /**
   * Track form submission error
   * @param {string} formId - Unique form identifier
   * @param {string} formTitle - Form title
   * @param {string} errorMessage - Error message
   * @param {object} additionalData - Additional data to track
   */
  trackFormError(formId, formTitle, errorMessage, additionalData = {}) {
    try {
      // Meta Pixel tracking
      if (this.isMetaPixelInitialized && window.fbq) {
        window.fbq('trackCustom', 'FormError', {
          form_id: formId,
          form_title: formTitle,
          error_message: errorMessage,
          ...additionalData
        });
      }

      // Google Analytics tracking
      if (this.isGoogleAnalyticsInitialized && window.gtag) {
        window.gtag('event', 'exception', {
          description: `Form submission error: ${errorMessage}`,
          fatal: false,
          form_id: formId,
          form_title: formTitle,
          ...additionalData
        });
      }

      if (this.debug) {
        console.log('Tracked form error:', { 
          formId, 
          formTitle, 
          errorMessage, 
          additionalData 
        });
      }
    } catch (error) {
      console.error('Error tracking form error:', error);
    }
  }

  /**
   * Track custom event
   * @param {string} eventName - Event name
   * @param {object} eventData - Event data
   */
  trackCustomEvent(eventName, eventData = {}) {
    try {
      // Meta Pixel tracking
      if (this.isMetaPixelInitialized && window.fbq) {
        window.fbq('trackCustom', eventName, eventData);
      }

      // Google Analytics tracking
      if (this.isGoogleAnalyticsInitialized && window.gtag) {
        window.gtag('event', eventName, eventData);
      }

      if (this.debug) {
        console.log('Tracked custom event:', { eventName, eventData });
      }
    } catch (error) {
      console.error('Error tracking custom event:', error);
    }
  }
}

// Export singleton instance
const trackingService = new TrackingService();
export default trackingService;